import { useEffect, useRef, useState } from "react";
import { VISOR_MODULE_BREADCRUMB_ROOT, CONCILIACION_MANUAL_CFDI_TITLE } from "@/modules/workModuleCore";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, detailFromAxiosError, fmtMoney } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Upload, FolderUp, FileSpreadsheet, Loader2, Database, FileText, Trash2, CheckCircle2, AlertTriangle, Wrench, RefreshCw, FileDown, ShieldCheck, XCircle, Building2, Table2, Info, ListChecks } from "lucide-react";
import { toast } from "sonner";
import ExcelCargaResumenPanel, { ExcelUploadIntentPicker } from "@/components/ExcelCargaResumenPanel";
import { evaluateUploadResult, formatExcelUploadSuccessMessage } from "@/lib/excelContabilidadPreflight";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  MAX_XML_BYTES,
  collectTooLargeMessages,
  partitionFilesByMaxSize,
} from "@/lib/fileSizeLimits";
import {
  folderUploadHasRelativePaths,
  xmlFilesFromDataTransfer,
  xmlFilesFromFileList,
} from "@/lib/cfdiFolderUpload";
import {
  EXCEL_FILE_ACCEPT,
  EXCEL_FOLDER_EMPTY_MSG,
  EXCEL_INVALID_FILE_MSG,
  EXCEL_NO_FILES_IN_FOLDER_MSG,
  EXCEL_PICK_FILE_MSG,
  EXCEL_PICK_FOLDER_MSG,
  ensureExcelFileInput,
  ensureExcelFolderInput,
  inferExcelUploadIntentFromFile,
  pickExcelFile,
  pickExcelFilesFromFolder,
  resolveExcelFiles,
} from "@/lib/excelFilePick";
import XmlPendingDetailsList from "@/components/confronta/XmlPendingDetailsList";
import ConciliacionManualCFDI from "@/pages/ConciliacionManualCFDI";

const XML_UPLOAD_BATCH = 150;
/** Por lote; con bulk_write + sin disco en nube suele bastar 3 min. */
const XML_UPLOAD_TIMEOUT_MS = 180000;

function normalizeHeaderCell(h) {
  if (h == null || h === "") return "";
  return String(h)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Sugerencia de mapeo CSV comparando encabezados con aliases del backend. */
function suggestMappingFromPreview(headers, fields) {
  const norm = headers.map(normalizeHeaderCell);
  const m = {};
  for (const f of fields) {
    const aliases = f.aliases || [];
    let found = false;
    for (let i = 0; i < norm.length && !found; i++) {
      for (const a of aliases) {
        const an = normalizeHeaderCell(a);
        if (!an) continue;
        if (norm[i] === an || norm[i].includes(an) || (norm[i].length > 1 && an.includes(norm[i]))) {
          m[f.key] = String(i);
          found = true;
          break;
        }
      }
    }
  }
  return m;
}

/** Columnas por hoja — plantilla simplificada 3 hojas (/reports/plantilla-excel-3-hojas). */
const PLANTILLA_3HOJAS_HEADERS = {
  Ingresos: [
    "Tipo", "Fecha de ingreso", "Fecha del documento", "Folio", "Cliente", "RFC",
    "Método de Pago",
    "Importe Total (Inc. Impuestos y Retenciones)",
    "Importe del Ingreso",
    "Ingreso cobrado tasa 16%",
    "IVA Efectivamente cobrado",
    "Moneda",
    "T.C.",
    "Total en pesos",
    "Folio Fiscal",
  ],
  Egresos: [
    "Tipo", "Fecha de egreso", "Fecha del documento", "Folio", "Proveedor", "RFC",
    "Método de Pago",
    "Importe Total (Inc. Impuestos y Retenciones)",
    "Importe del egreso",
    "Egreso pagado tasa 16%",
    "IVA Efectivamente pagado",
    "Retención de ISR Efectivamente pagada",
    "Moneda",
    "T.C.",
    "Total en pesos",
    "Folio Fiscal",
  ],
  Nominas: [
    "Tipo", "Fecha de egreso", "Fecha del documento", "Folio", "Proveedor", "RFC",
    "Método de Pago",
    "Importe Total (Inc. Impuestos y Retenciones)",
    "Importe del egreso",
    "Percepciones gravadas", "Percepciones exentas",
    "ISR Retenido de Nómina",     "Otras deducciones",
    "Retención de ISR Efectivamente pagada",
    "Moneda",
    "T.C.",
    "Total en pesos",
    "Folio Fiscal",
  ],
};

/**
 * Descarga plantilla/informe vía API autenticada (cookies + Bearer).
 * `window.open` en una pestaña nueva a menudo no envía Authorization y puede
 * devolver HTML/JSON en lugar del .xlsx (un solo “Sheet1” o archivo inválido).
 */
async function downloadReportBlob(path, params, fallbackFilename) {
  try {
    const { data, headers } = await api.get(path, {
      responseType: "blob",
      params: params ?? undefined,
    });
    if (!(data instanceof Blob) || data.size === 0) {
      toast.error("El servidor devolvió un archivo vacío. Revisa la sesión o la pestaña Red (F12).");
      return;
    }
    const head = await data.slice(0, 8).arrayBuffer();
    const u8 = new Uint8Array(head);
    const looksZip = u8.length >= 4 && u8[0] === 0x50 && u8[1] === 0x4b; // .xlsx / .docx
    const startTxt = new TextDecoder("utf-8", { fatal: false }).decode(u8.slice(0, 4));
    const probablyJson = startTxt.trimStart().startsWith("{") || startTxt.trimStart().startsWith("[");
    if (probablyJson && !looksZip) {
      const text = await data.text();
      try {
        const j = JSON.parse(text);
        toast.error(formatApiError(j.detail) || text.slice(0, 400) || "Respuesta inválida al descargar");
      } catch {
        toast.error(text.slice(0, 400) || "Respuesta inválida al descargar");
      }
      return;
    }
    const cd = headers["content-disposition"] || headers["Content-Disposition"] || "";
    let name = fallbackFilename;
    const star = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
    const quoted = cd.match(/filename="([^"]+)"/i);
    if (star) {
      try {
        name = decodeURIComponent(star[1]);
      } catch {
        name = star[1];
      }
    } else if (quoted) {
      name = quoted[1];
    }
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Descarga: ${name}`);
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  } catch (e) {
    const msg = await detailFromAxiosError(e);
    toast.error(msg || formatApiError(e.response?.data?.detail) || "No se pudo descargar el archivo");
  }
}

const EXCEL_SOURCE_LABELS = {
  control_iva: "Carga de Reporte en Excel (Contalink / Control IVA)",
  plantilla_3h: "Plantilla 3 hojas",
  csv: "CSV / otros",
  modo_complemento: CONCILIACION_MANUAL_CFDI_TITLE,
  legacy: "Histórico sin medio",
};

function formatExcelMonthsBySource(monthsBySource) {
  if (!monthsBySource || typeof monthsBySource !== "object") return [];
  return Object.keys(monthsBySource)
    .sort()
    .map((ym) => {
      const parts = monthsBySource[ym] || {};
      const bits = Object.entries(parts).map(([k, n]) => `${EXCEL_SOURCE_LABELS[k] || k}: ${n}`);
      return `${ym} → ${bits.join(", ")}`;
    });
}

function hasMixedExcelSourcesInOneMonth(monthsBySource) {
  if (!monthsBySource || typeof monthsBySource !== "object") return false;
  return Object.values(monthsBySource).some((parts) => parts && Object.keys(parts).length > 1);
}

/**
 * Centro de carga de información:
 * - Secciones plegables: CFDI XML, Excel, plantillas Excel, carga por CSV y conciliación manual para confronta.
 */
export default function CargaInformacion() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openSections, setOpenSections] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [year, setYear] = useState(() => {
    const saved = Number(localStorage.getItem("vf:selected_year") || 0);
    if (saved >= 2020 && saved <= 2099) return saved;
    const now = new Date();
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [direccion, setDireccion] = useState("emitido");

  const pageTitle = "Carga de Información";
  const pageSubtitle =
    "Centro de carga manual: XML, Excel Contalink/Control IVA, plantillas para cargas en Excel, CSV con mapeo y conciliación manual para confronta. La descarga de XML del SAT con e.Firma está en «Descarga de CFDI SAT».";
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [excelStats, setExcelStats] = useState(null);
  const [cfdiStats, setCfdiStats] = useState(null);
  const [health, setHealth] = useState(null);

  const fileRef = useRef(null);
  const folderRef = useRef(null);
  const excelRef = useRef(null);
  const excelPlantillaRef = useRef(null);
  const excelFolderRef = useRef(null);
  const csvRef = useRef(null);
  const [fieldCatalog, setFieldCatalog] = useState([]);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvMapping, setCsvMapping] = useState({});
  const [csvDireccion, setCsvDireccion] = useState("ingreso");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvFileHold, setCsvFileHold] = useState(null);
  const [excelUploadIntent, setExcelUploadIntent] = useState(() => {
    try {
      const s = localStorage.getItem("vf:excel_upload_intent");
      if (s === "ingreso" || s === "egreso" || s === "nomina" || s === "mixto_3h") return s;
    } catch { /* ignore */ }
    return "ingreso";
  });
  const [tooLargeDialog, setTooLargeDialog] = useState({ open: false, items: [] });
  /** Panel de avance de carga XML: open, minimized, status, lotes y contadores. */
  const [xmlProgress, setXmlProgress] = useState(null);
  const xmlAbortRef = useRef(null);
  /** Progreso al subir uno o varios Excel (archivo o carpeta). */
  const [excelProgress, setExcelProgress] = useState(null);
  /** Mensaje fijo bajo los botones (no depende solo de toast). */
  const [excelStatusMsg, setExcelStatusMsg] = useState("");

  /** Bloquea otras cargas mientras hay XML, Excel o CSV en curso. */
  const dataLoadBusy = busy || csvBusy;

  // Activa webkitdirectory SOLO en el input de carpeta XML (nunca en Excel).
  const folderInputRef = (el) => {
    folderRef.current = el;
    if (el) {
      el.setAttribute("webkitdirectory", "");
      el.setAttribute("directory", "");
    }
  };

  /** Input Excel de Contalink: garantiza selector de archivo, no carpeta. */
  const excelInputRef = (el) => {
    excelRef.current = ensureExcelFileInput(el);
  };

  /** Input Excel de plantilla 3 hojas: mismo tratamiento. */
  const excelPlantillaInputRef = (el) => {
    excelPlantillaRef.current = ensureExcelFileInput(el);
  };

  /** Input carpeta: webkitdirectory solo aquí (varios Contalink en una carpeta). */
  const excelFolderInputRef = (el) => {
    excelFolderRef.current = ensureExcelFolderInput(el);
  };

  /** Intent forzado al abrir el picker (plantilla 3h); cubre el fallback vía <input onChange>. */
  const excelIntentOverrideRef = useRef(null);

  useEffect(() => {
    if ((searchParams.get("open") || "").toLowerCase() === "sat") {
      navigate("/descarga-automatica-sat?tab=fiel", { replace: true });
      return;
    }
    const o = (searchParams.get("open") || "").toLowerCase();
    if (o === "conciliacion-manual" || o === "conciliacion" || o === "modo-complemento") {
      setOpenSections((prev) => (prev.includes("conciliacion-manual") ? prev : [...prev, "conciliacion-manual"]));
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    api.get("/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        setClients([]);
        toast.error("Error al cargar clientes");
      });
  }, []);

  useEffect(() => {
    api.get("/excel/import-field-catalog").then(({ data }) => setFieldCatalog(data.fields || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId || !year) return;
    localStorage.setItem("vf:selected_year", String(year));
    loadStats();
    // eslint-disable-next-line
  }, [clientId, year, direccion]);

  const loadStats = async () => {
    if (!clientId) return;
    try {
      const { data: cfdi } = await api.get("/cfdi/summary", { params: { client_id: clientId, year } });
      setCfdiStats(cfdi);
    } catch { setCfdiStats(null); }
    try {
      const { data: chk } = await api.get("/excel/check-existing", { params: { client_id: clientId, year } });
      setExcelStats(chk);
    } catch { setExcelStats(null); }
  };

  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [pendingXmlDetails, setPendingXmlDetails] = useState([]);
  const [xmlDropActive, setXmlDropActive] = useState(false);

  const runReconcileIntegridad = async () => {
    if (!clientId) {
      toast.error("Selecciona un cliente");
      return;
    }
    setReconcileBusy(true);
    try {
      const { data } = await api.post("/sat/cfdi/reconcile-xml", {
        client_id: clientId,
        trigger_healing: false,
      });
      const linked = Number(data.reconciled) || 0;
      const pending =
        Number(data.xml_pending_integration) ||
        Number(data.xml_integrity?.pending_integration) ||
        0;
      const details = Array.isArray(data.pending_details) ? data.pending_details : [];
      setPendingXmlDetails(details);
      if (linked > 0) {
        toast.success(
          `${linked.toLocaleString("es-MX")} comprobante(s) integrados: el XML ya estaba en la base de datos.`,
        );
      } else if (pending > 0) {
        toast.info(
          `Quedan ${pending.toLocaleString("es-MX")} pendiente(s) sin archivo. Revise la lista inferior con fecha y UUID.`,
        );
      } else {
        setPendingXmlDetails([]);
        toast.success("Integridad XML al día: no hay comprobantes pendientes.");
      }
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e) || "No se pudo recalcular la integridad XML");
    } finally {
      setReconcileBusy(false);
    }
  };

  const closeXmlProgress = () => {
    setXmlProgress(null);
  };

  const cancelXmlUpload = () => {
    try {
      xmlAbortRef.current?.abort();
    } catch { /* ignore */ }
    setXmlProgress((prev) => (
      prev
        ? { ...prev, status: "cancelling", minimized: false }
        : prev
    ));
  };

  const continueXmlProgress = () => {
    setXmlProgress((prev) => {
      if (!prev) return prev;
      if (prev.status === "running" || prev.status === "cancelling") {
        return { ...prev, minimized: true };
      }
      return null;
    });
  };

  const doUploadXML = async (rawFiles, options = {}) => {
    if (!clientId) { toast.error("Selecciona un cliente"); return; }
    const xmls = xmlFilesFromFileList(rawFiles);
    if (!xmls.length) { toast.error("No se encontraron archivos .xml"); return; }

    const { accepted, rejected } = partitionFilesByMaxSize(xmls, MAX_XML_BYTES);
    const clientRejected = rejected;

    if (!accepted.length) {
      const items = collectTooLargeMessages(clientRejected, []);
      setTooLargeDialog({ open: true, items });
      return;
    }

    const allYears = options.allYears === true
      || (options.allYears !== false && folderUploadHasRelativePaths(accepted));

    const controller = new AbortController();
    xmlAbortRef.current = controller;

    setBusy(true);
    setLastResult(null);
    const BATCH = XML_UPLOAD_BATCH;
    const totalBatches = Math.max(1, Math.ceil(accepted.length / BATCH));
    let agg = {
      inserted: 0, duplicated: 0, errors: 0, skipped_rfc: 0, skipped_year: 0,
      skipped_too_large: 0, total: 0, marked_cancelled_new: 0, marked_cancelled_existing: 0,
      reconciled_integrity: 0, xml_pending_integration: null,
      client_rfc: "",
      skipped_details: [],
    };
    const serverTooLarge = [];
    let wasCancelled = false;

    setXmlProgress({
      open: true,
      minimized: false,
      status: "running",
      batch: 0,
      totalBatches,
      filesTotal: accepted.length,
      inserted: 0,
      duplicated: 0,
      errors: 0,
    });

    try {
      for (let i = 0; i < accepted.length; i += BATCH) {
        if (controller.signal.aborted) {
          wasCancelled = true;
          break;
        }
        const batchIndex = Math.floor(i / BATCH) + 1;
        setXmlProgress((prev) => (
          prev
            ? {
              ...prev,
              status: "running",
              batch: batchIndex,
              inserted: agg.inserted,
              duplicated: agg.duplicated,
              errors: agg.errors,
            }
            : prev
        ));
        const slice = accepted.slice(i, i + BATCH);
        const fd = new FormData();
        fd.append("client_id", clientId);
        fd.append("year", String(year));
        fd.append("all_years", allYears ? "true" : "false");
        fd.append("sync_mode", "false");
        slice.forEach(f => {
          fd.append("files", f);
          fd.append("paths", f.webkitRelativePath || f.name);
        });
        const { data } = await api.post("/cfdi/upload", fd, {
          timeout: XML_UPLOAD_TIMEOUT_MS,
          signal: controller.signal,
        });
        agg.inserted += data.inserted;
        agg.duplicated += data.duplicated;
        agg.errors += data.errors;
        agg.skipped_rfc += data.skipped_rfc || 0;
        agg.skipped_year += data.skipped_year || 0;
        agg.skipped_too_large += data.skipped_too_large || 0;
        agg.marked_cancelled_new += data.marked_cancelled_new || 0;
        agg.marked_cancelled_existing += data.marked_cancelled_existing || 0;
        agg.reconciled_integrity += data.reconciled_integrity || 0;
        if (data.client_rfc) agg.client_rfc = data.client_rfc;
        if (Array.isArray(data.skipped_details) && agg.skipped_details.length < 8) {
          agg.skipped_details.push(...data.skipped_details.slice(0, 8 - agg.skipped_details.length));
        }
        if (data.xml_pending_integration != null) {
          agg.xml_pending_integration = data.xml_pending_integration;
        }
        agg.total += data.total;
        if (Array.isArray(data.file_too_large_details)) {
          serverTooLarge.push(...data.file_too_large_details);
        }
        setXmlProgress((prev) => (
          prev
            ? {
              ...prev,
              batch: batchIndex,
              inserted: agg.inserted,
              duplicated: agg.duplicated,
              errors: agg.errors,
            }
            : prev
        ));
      }

      if (controller.signal.aborted) {
        wasCancelled = true;
      }

      setLastResult({ kind: "xml", ...agg, all_years: allYears, cancelled: wasCancelled });
      const tooLargeItems = collectTooLargeMessages(clientRejected, serverTooLarge);
      if (tooLargeItems.length) {
        setTooLargeDialog({ open: true, items: tooLargeItems });
      }

      if (wasCancelled) {
        setXmlProgress((prev) => (
          prev
            ? {
              ...prev,
              status: "cancelled",
              minimized: false,
              inserted: agg.inserted,
              duplicated: agg.duplicated,
              errors: agg.errors,
            }
            : prev
        ));
        toast.info(
          `Carga cancelada: ${agg.inserted} nuevos, ${agg.duplicated} actualizados hasta el momento.`,
          { duration: 8000 },
        );
        if (agg.inserted || agg.duplicated) loadStats();
      } else {
        setXmlProgress((prev) => (
          prev
            ? {
              ...prev,
              status: "done",
              batch: totalBatches,
              inserted: agg.inserted,
              duplicated: agg.duplicated,
              errors: agg.errors,
              minimized: false,
            }
            : prev
        ));
        if (agg.inserted || agg.duplicated) {
          let msg = `XMLs cargados: ${agg.inserted} nuevos, ${agg.duplicated} actualizados`;
          if (agg.reconciled_integrity > 0) {
            msg += `; ${agg.reconciled_integrity} integrados en la base de datos`;
          }
          if (allYears) msg += " (todos los ejercicios de la carpeta)";
          toast.success(msg);
          if (agg.skipped_rfc || agg.skipped_year) {
            toast.warning(
              [
                agg.skipped_rfc ? `${agg.skipped_rfc} omitido(s) por RFC distinto al cliente` : null,
                agg.skipped_year ? `${agg.skipped_year} por año ≠ ejercicio ${year}` : null,
              ].filter(Boolean).join("; ")
              + (agg.client_rfc ? ` (RFC cliente: ${agg.client_rfc}).` : "."),
              { duration: 10000 },
            );
          }
        } else if (agg.skipped_year || agg.skipped_rfc) {
          const samples = (agg.skipped_details || []).slice(0, 3).map((row) => {
            if (row.reason === "year_mismatch") {
              return `${row.filename || "XML"}: año ${row.xml_year} (ejercicio ${row.expected})`;
            }
            if (row.reason === "rfc_mismatch") {
              return `${row.filename || "XML"}: ${row.rfc_xml || "sin RFC"} ≠ ${row.expected || agg.client_rfc || "?"}`;
            }
            return row.filename || "XML";
          });
          toast.warning(
            `Ningún XML nuevo: ${agg.skipped_year} por año distinto al ejercicio ${year}, `
            + `${agg.skipped_rfc} por RFC`
            + (agg.client_rfc ? ` (cliente ${agg.client_rfc})` : "")
            + ". "
            + (samples.length ? `Ej.: ${samples.join(" · ")}. ` : "")
            + "Use «Cargar XML» (carpeta) para varios años, o revise el RFC del cliente en Clientes.",
            { duration: 14000 },
          );
        } else if (agg.errors > 0) {
          toast.error(
            `No se pudieron procesar ${agg.errors.toLocaleString("es-MX")} archivo(s). Revise que sean XML CFDI válidos del cliente seleccionado.`,
          );
        } else if (!tooLargeItems.length) {
          toast.warning("No se insertaron XML nuevos en esta carga");
        }
        loadStats();
      }
    } catch (e) {
      const aborted = e?.code === "ERR_CANCELED"
        || e?.name === "CanceledError"
        || controller.signal.aborted
        || String(e?.message || "").toLowerCase().includes("cancel");
      if (aborted) {
        wasCancelled = true;
        setLastResult({ kind: "xml", ...agg, all_years: allYears, cancelled: true });
        setXmlProgress((prev) => (
          prev
            ? {
              ...prev,
              status: "cancelled",
              minimized: false,
              inserted: agg.inserted,
              duplicated: agg.duplicated,
              errors: agg.errors,
            }
            : prev
        ));
        toast.info(
          `Carga cancelada: ${agg.inserted} nuevos, ${agg.duplicated} actualizados hasta el momento.`,
          { duration: 8000 },
        );
        if (agg.inserted || agg.duplicated) loadStats();
      } else {
        const detail = e.response?.data?.detail ?? detailFromAxiosError(e);
        setXmlProgress((prev) => (
          prev
            ? { ...prev, status: "error", minimized: false }
            : prev
        ));
        if (detail?.code === "FILE_TOO_LARGE") {
          setTooLargeDialog({
            open: true,
            items: collectTooLargeMessages(clientRejected, [detail]),
          });
        } else if (e.code === "ECONNABORTED" || String(e.message || "").includes("timeout")) {
          toast.error(
            "La carga tardó demasiado en un lote. Intente por partes (menos archivos) o vuelva a usar «Cargar XML».",
            { duration: 12000 },
          );
        } else {
          toast.error(formatApiError(e) || formatApiError(detail) || "Error al cargar XML");
        }
      }
    } finally {
      xmlAbortRef.current = null;
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (folderRef.current) folderRef.current.value = "";
    }
  };

  const postOneExcelFile = async (file, intentOverride) => {
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("year", String(year));
    fd.append("upload_intent", intentOverride || excelUploadIntent);
    fd.append("file", file);
    const { data } = await api.post("/excel/upload", fd);
    return data;
  };

  const summarizeExcelUploadData = (data, intentForMsg, fileName) => {
    return formatExcelUploadSuccessMessage(data, intentForMsg || excelUploadIntent, {
      loadedAt: new Date(),
      fileName,
    });
  };

  const applyExcelUploadSideEffects = (data, intentForEval) => {
    const prev = data.import_preview;
    if (prev && data.total >= 5 && (prev.total || 0) < 1) {
      toast.warning(
        "Las filas se guardaron pero los importes suman $0. Revise que exportó la hoja de datos (Egresos/Ingresos) desde Contalink, no solo Resumen.",
        { duration: 12000 },
      );
    }
    const resIng = data.carga_resumen?.ingreso;
    if (resIng?.totales?.total > 0.01) {
      const meses = Object.keys(resIng.por_mes || {}).length;
      toast.info(
        `Ingresos en BD: ${resIng.filas} fila(s), ${meses} mes(es), total ${fmtMoney(resIng.totales.total)}. Ejecute confronta de nuevo.`,
        { duration: 12000 },
      );
    }
    const uploadEval = evaluateUploadResult(intentForEval || excelUploadIntent, data);
    if (uploadEval.intentAlreadyInBd) {
      uploadEval.issues.forEach((issue) => toast.success(issue.message, { duration: 14000 }));
    } else if (uploadEval.severity === "block") {
      uploadEval.issues.forEach((issue) => toast.error(issue.message, { duration: 16000 }));
    } else if (
      data.upload_warning?.message
      && data.upload_warning?.code !== "EXCEL_ARCHIVO_PARECE_EGRESOS"
    ) {
      toast.warning(data.upload_warning.message, { duration: 14000 });
    } else if (uploadEval.severity === "warn") {
      uploadEval.issues.forEach((issue) => toast.warning(issue.message, { duration: 12000 }));
    }
  };

  /** Sube uno o varios Excel (archivo suelto o carpeta Contalink) con progreso visible. */
  const uploadExcelFiles = async (files, intentOverride) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) {
      setExcelStatusMsg("No hay archivos Excel para cargar.");
      return;
    }
    if (!clientId) { toast.error("Selecciona cliente"); return; }

    setBusy(true);
    setLastResult(null);
    setExcelStatusMsg(`Preparando carga de ${list.length} Excel…`);
    let lastData = null;
    let lastOkIntent = null;
    let lastOkFile = null;
    let okCount = 0;
    let failCount = 0;
    const intent = intentOverride || excelUploadIntent;
    // En carpeta: inferir Ingresos/Egresos por nombre para no filtrar a 0 filas.
    const multiFolder = list.length > 1;

    try {
      for (let i = 0; i < list.length; i += 1) {
        const file = list[i];
        const fileIntent = multiFolder
          ? inferExcelUploadIntentFromFile(file, intent)
          : intent;
        const progressLabel = list.length > 1
          ? `Cargando Excel ${i + 1}/${list.length}: ${file.name}`
          : `Cargando Excel: ${file.name}`;
        setExcelProgress({
          current: i + 1,
          total: list.length,
          name: file.name,
        });
        setExcelStatusMsg(progressLabel);
        toast.info(progressLabel, { id: "excel-upload-progress", duration: 60000 });
        try {
          const data = await postOneExcelFile(file, fileIntent);
          // total=0 no debe verse como éxito (Última carga no cambia).
          if (!Number(data?.total) || Number(data.total) < 1) {
            failCount += 1;
            const emptyMsg =
              `«${file.name}» no guardó filas (0). `
              + (multiFolder
                ? "Revise que el nombre indique Ingresos/Egresos o elija la opción correcta."
                : `Con la opción actual no hubo filas compatibles. Elija Ingresos o Egresos según el archivo.`);
            setExcelStatusMsg(`Error — ${emptyMsg}`);
            toast.error(emptyMsg, {
              id: list.length === 1 ? "excel-upload-progress" : undefined,
              duration: 14000,
            });
            continue;
          }
          lastData = data;
          lastOkIntent = fileIntent;
          lastOkFile = file.name;
          okCount += 1;
          setLastResult({ kind: "excel", ...data, batch_file: file.name, batch_index: i + 1, batch_total: list.length });
          if (list.length === 1) {
            const okMsg = summarizeExcelUploadData(data, fileIntent, file.name);
            setExcelStatusMsg(okMsg);
            toast.success(okMsg, { id: "excel-upload-progress", duration: 16000 });
            applyExcelUploadSideEffects(data, fileIntent);
          }
        } catch (e) {
          failCount += 1;
          const st = e.response?.status;
          const detail = e.response?.data?.detail;
          const errMsg = st === 409 && detail?.code === "EXCEL_MONTH_SOURCE_CONFLICT"
            ? (formatApiError(detail) || "Conflicto de medios por mes.")
            : (formatApiError(detail) || `Error al cargar «${file.name}»`);
          setExcelStatusMsg(`Error — ${errMsg}`);
          toast.error(errMsg, { id: list.length === 1 ? "excel-upload-progress" : undefined, duration: 14000 });
        }
      }
      if (list.length > 1) {
        const summary = okCount
          ? (
            `Carga de carpeta exitosa: ${okCount} archivo(s)`
            + (failCount ? `, ${failCount} con error` : "")
            + (lastData
              ? `. Último — ${summarizeExcelUploadData(lastData, lastOkIntent, lastOkFile)}`
              : ".")
          )
          : `Carpeta Excel: ningún archivo se cargó (${failCount} con error).`;
        setExcelStatusMsg(summary);
        if (okCount) toast.success(summary, { id: "excel-upload-progress", duration: 18000 });
        else toast.error(summary, { id: "excel-upload-progress", duration: 14000 });
        if (lastData && okCount) applyExcelUploadSideEffects(lastData, lastOkIntent || intent);
      }
      if (okCount) loadStats();
    } finally {
      setBusy(false);
      setExcelProgress(null);
      if (excelRef.current) excelRef.current.value = "";
      if (excelPlantillaRef.current) excelPlantillaRef.current.value = "";
      if (excelFolderRef.current) excelFolderRef.current.value = "";
    }
  };

  const uploadExcelFile = async (file, intentOverride) => {
    if (!file) return;
    await uploadExcelFiles([file], intentOverride);
  };

  const onExcelUpload = async (e) => {
    const resolved = resolveExcelFiles(e.target.files);
    e.target.value = "";
    if (!resolved.ok) {
      excelIntentOverrideRef.current = null;
      if (resolved.reason === "empty") return;
      toast.error(
        resolved.reason === "no_excel" ? EXCEL_NO_FILES_IN_FOLDER_MSG : EXCEL_INVALID_FILE_MSG,
        { duration: 10000 },
      );
      return;
    }
    const override = excelIntentOverrideRef.current;
    excelIntentOverrideRef.current = null;
    await uploadExcelFiles(resolved.files, override || undefined);
  };

  const onExcelFolderUpload = async (e) => {
    const list = e.target.files;
    const resolved = resolveExcelFiles(list);
    const totalPicked = list?.length || 0;
    try {
      e.target.value = "";
    } catch {
      /* ignore */
    }
    if (!resolved.ok) {
      if (resolved.reason === "empty") {
        setExcelStatusMsg(EXCEL_FOLDER_EMPTY_MSG);
        toast.error(EXCEL_FOLDER_EMPTY_MSG, { duration: 10000 });
        return;
      }
      const msg = `${EXCEL_NO_FILES_IN_FOLDER_MSG} (se vieron ${totalPicked} archivo(s) en la carpeta).`;
      setExcelStatusMsg(msg);
      toast.error(msg, { duration: 14000 });
      return;
    }
    const startMsg = `Carpeta: ${resolved.files.length} Excel detectado(s). Iniciando carga…`;
    setExcelStatusMsg(startMsg);
    toast.info(startMsg, { id: "excel-upload-progress", duration: 8000 });
    await uploadExcelFiles(resolved.files);
  };

  const openExcelFilePicker = async (which = "contalink") => {
    if (!clientId) {
      toast.error(
        clients.length
          ? "Seleccione una empresa arriba para cargar Excel Contalink."
          : "No hay empresas asignadas. Pida al administrador de equipo que le asigne clientes.",
      );
      return;
    }
    const inputEl = which === "plantilla" ? excelPlantillaRef.current : excelRef.current;
    if (!inputEl) {
      const msg = "No se pudo abrir el selector de archivo. Recargue la página e intente de nuevo.";
      setExcelStatusMsg(msg);
      toast.error(msg);
      return;
    }
    const intentOverride = which === "plantilla" ? "mixto_3h" : undefined;
    if (intentOverride) {
      setExcelUploadIntent(intentOverride);
      try {
        localStorage.setItem("vf:excel_upload_intent", intentOverride);
      } catch {
        /* ignore */
      }
    }
    excelIntentOverrideRef.current = intentOverride || null;
    setExcelStatusMsg(EXCEL_PICK_FILE_MSG);
    toast.info(EXCEL_PICK_FILE_MSG, { id: "excel-upload-progress", duration: 8000 });
    // Preferir <input type=file>: el File System Access API falla en silencio en el preview del IDE.
    const file = await pickExcelFile(inputEl, { preferFileSystemAccess: false });
    if (file) {
      excelIntentOverrideRef.current = null;
      await uploadExcelFiles([file], intentOverride);
    } else {
      // El archivo llega por onChange del input (camino fiable).
      setExcelStatusMsg(EXCEL_PICK_FILE_MSG);
    }
  };

  const openExcelFolderPicker = async () => {
    if (!clientId) {
      toast.error(
        clients.length
          ? "Seleccione una empresa arriba para cargar la carpeta Excel."
          : "No hay empresas asignadas. Pida al administrador de equipo que le asigne clientes.",
      );
      return;
    }
    if (!excelFolderRef.current) {
      const msg = "No se pudo abrir el selector de carpeta. Recargue la página e intente de nuevo.";
      setExcelStatusMsg(msg);
      toast.error(msg);
      return;
    }
    ensureExcelFolderInput(excelFolderRef.current);
    setExcelStatusMsg(EXCEL_PICK_FOLDER_MSG);
    toast.info(EXCEL_PICK_FOLDER_MSG, { id: "excel-upload-progress", duration: 8000 });
    // Preferir webkitdirectory: showDirectoryPicker suele no devolver archivos en el preview.
    const result = await pickExcelFilesFromFolder(excelFolderRef.current, {
      preferDirectoryPicker: false,
    });
    if (!result || result.status === "cancelled") {
      setExcelStatusMsg(result?.message || "Selección de carpeta cancelada.");
      toast.info(result?.message || "Selección de carpeta cancelada.", { id: "excel-upload-progress" });
      return;
    }
    if (result.status === "deferred") {
      setExcelStatusMsg(result.message || EXCEL_PICK_FOLDER_MSG);
      return;
    }
    if (result.status === "error" || result.status === "empty") {
      setExcelStatusMsg(result.message || EXCEL_NO_FILES_IN_FOLDER_MSG);
      toast.error(result.message || EXCEL_NO_FILES_IN_FOLDER_MSG, { duration: 14000, id: "excel-upload-progress" });
      return;
    }
    const files = result.files || [];
    const startMsg = `Carpeta: ${files.length} Excel detectado(s). Iniciando carga…`;
    setExcelStatusMsg(startMsg);
    toast.info(startMsg, { id: "excel-upload-progress", duration: 8000 });
    await uploadExcelFiles(files);
  };

  const onCsvFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileHold(file);
    setCsvBusy(true);
    setCsvPreview(null);
    setCsvMapping({});
    try {
      let fields = fieldCatalog;
      if (!fields.length) {
        const { data: cat } = await api.get("/excel/import-field-catalog");
        fields = cat.fields || [];
        setFieldCatalog(fields);
      }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/excel/csv-preview", fd);
      setCsvPreview(data);
      const sug = suggestMappingFromPreview(data.headers, fields);
      setCsvMapping(sug);
      toast[Object.keys(sug).length ? "success" : "info"](
        Object.keys(sug).length
          ? "Vista previa lista. Revisa y ajusta el mapeo si hace falta."
          : "Vista previa lista. Asigna columnas manualmente."
      );
    } catch (err) {
      setCsvFileHold(null);
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo leer el CSV");
    } finally {
      setCsvBusy(false);
    }
  };

  const submitCsvUpload = async () => {
    if (!clientId) { toast.error("Selecciona cliente"); return; }
    if (!csvPreview?.headers?.length) { toast.error("Primero sube el CSV para ver la vista previa"); return; }
    const tipoI = csvMapping.tipo;
    const ffI = csvMapping.folio_fiscal;
    if (tipoI === undefined || tipoI === "" || ffI === undefined || ffI === "") {
      toast.error("Debes mapear al menos Tipo y Folio fiscal (UUID)");
      return;
    }
    setBusy(true);
    setLastResult(null);
    if (!csvFileHold) {
      toast.error("Selecciona el CSV de nuevo (vista previa)");
      setBusy(false);
      return;
    }
    const mappingObj = {};
    for (const [k, v] of Object.entries(csvMapping)) {
      if (v !== "" && v != null && v !== undefined) mappingObj[k] = parseInt(String(v), 10);
    }
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("year", String(year));
    fd.append("direccion", csvDireccion);
    fd.append("mapping", JSON.stringify(mappingObj));
    if (csvPreview?.delimiter) fd.append("delimiter", String(csvPreview.delimiter));
    fd.append("file", csvFileHold);
    try {
      const { data } = await api.post("/excel/upload-csv", fd);
      setLastResult({ kind: "csv", ...data });
      let msg = `CSV cargado: ${data.inserted} nuevos, ${data.updated} actualizados (${data.total} filas).`;
      if (Array.isArray(data.replaced_months) && data.replaced_months.length) {
        msg += ` Meses reemplazados: ${data.replaced_months.join(", ")}.`;
      }
      toast.success(msg);
      loadStats();
      setCsvPreview(null);
      setCsvMapping({});
      setCsvFileHold(null);
      if (csvRef.current) csvRef.current.value = "";
    } catch (e) {
      const st = e.response?.status;
      const detail = e.response?.data?.detail;
      if (st === 409 && detail?.code === "EXCEL_MONTH_SOURCE_CONFLICT") {
        toast.error(formatApiError(detail) || "Conflicto de medios por mes.");
      } else {
        toast.error(formatApiError(detail) || "Error al cargar CSV");
      }
    } finally {
      setBusy(false);
    }
  };

  const clearYear = async (what) => {
    if (!clientId) return;
    const msg = what === "excel"
      ? `¿Eliminar TODAS las filas del Excel del contador para el año ${year}?`
      : `¿Eliminar TODOS los CFDIs (Emitidos y Recibidos) del año ${year}?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      if (what === "excel") {
        await api.delete("/excel/clear", { params: { client_id: clientId, year }});
      } else {
        // Al limpiar CFDI, ahora limpiamos ambos por defecto o pedimos confirmación
        await api.delete("/cfdi/clear", { params: { client_id: clientId, year, direccion: "emitido" }});
        await api.delete("/cfdi/clear", { params: { client_id: clientId, year, direccion: "recibido" }});
      }
      toast.success("Eliminado correctamente");
      loadStats();
    } catch {
      toast.error("No se pudo eliminar");
    } finally { setBusy(false); }
  };

  const cli = clients.find(c => c.id === clientId);
  const totalCfdi = (cfdiStats || []).reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div className="min-h-screen">
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, pageTitle]}
      />

      <div className="p-8 space-y-5">
        {/* Selector común: cliente + año */}
        <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="carga-client-select" className="mt-1 h-auto min-h-[40px] py-2 text-left">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent className="max-w-[600px]">
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id} className="py-2">
                      <div className="flex flex-col items-start text-left">
                        <div className="font-bold text-sm whitespace-normal leading-tight">{c.razon_social}</div>
                        <div className="text-[10px] text-slate-500 mt-1 font-mono">
                          {c.rfc} {c.owner_email ? ` · ${c.owner_email}` : ""}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!clients.length ? (
                <p className="text-[11px] text-amber-800 mt-1.5 m-0" data-testid="carga-sin-empresas-aviso">
                  No hay empresas visibles. Auxiliares solo ven empresas asignadas o que ellos dieron de alta;
                  pida asignación al administrador de equipo. Sin empresa seleccionada no se puede cargar Excel Contalink.
                </p>
              ) : !clientId ? (
                <p className="text-[11px] text-amber-800 mt-1.5 m-0" data-testid="carga-seleccione-empresa-aviso">
                  Seleccione una empresa arriba para habilitar <strong>Cargar Excel</strong> (Contalink / Control IVA).
                  Auxiliares y administradores de equipo pueden cargar si tienen acceso a esa empresa.
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Ejercicio</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                <SelectTrigger data-testid="carga-year-select" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-end gap-3 text-xs">
            <div className="flex-1 bg-slate-50 rounded-sm p-2.5 border border-slate-200">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">CFDIs cargados ({year})</div>
              <div className="text-lg font-bold text-[#0F2D5C]">{totalCfdi}</div>
              <div className="text-[10px] text-slate-400">
                {(cfdiStats || []).map((r) => {
                  const tipo = r.tipo || r.tipo_comprobante || "?";
                  return `${r.direccion || "?"} ${tipo}: ${r.count}`;
                }).join(" · ")}
              </div>
            </div>
            <div className="flex-1 bg-slate-50 rounded-sm p-2.5 border border-slate-200">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Filas Excel ({year})</div>
              <div className="text-lg font-bold text-[#0F2D5C]">{excelStats?.count || 0}</div>
              <div className="text-[10px] text-slate-400">
                {excelStats?.count > 0 ? `${Object.keys(excelStats.months || {}).length} meses` : "Sin datos cargados"}
              </div>
            </div>
          </div>

          {excelStats?.count > 0 && (
            <div className="space-y-2 text-xs">
              {hasMixedExcelSourcesInOneMonth(excelStats.months_by_source) && (
                <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm p-2">
                  Al menos un mes tiene más de un medio registrado (histórico mezclado). Revise confronta o use «Limpiar año» para Excel antes de seguir.
                </div>
              )}
              <div className="text-[10px] text-slate-600 border border-slate-100 rounded-sm p-2 bg-slate-50/80 max-h-36 overflow-y-auto">
                <span className="font-semibold text-slate-700">Contabilidad importada por mes:</span>
                <ul className="mt-1 list-disc list-inside space-y-0.5 font-mono leading-snug">
                  {formatExcelMonthsBySource(excelStats.months_by_source).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={setOpenSections}
          className="rounded-sm border border-slate-200 bg-white shadow-sm"
        >
          <AccordionItem value="xml" className="border-slate-200 px-0">
            <AccordionTrigger data-testid="tab-carga-xml">
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Carga manual de CFDI
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4">
            <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
              <div
                className={`bg-slate-50 border border-dashed rounded-md p-8 text-center transition-colors ${
                  xmlDropActive
                    ? "border-[#0F2D5C] bg-blue-50/80"
                    : "border-slate-200"
                }`}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setXmlDropActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setXmlDropActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!e.currentTarget.contains(e.relatedTarget)) setXmlDropActive(false);
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setXmlDropActive(false);
                  if (dataLoadBusy || !clientId) return;
                  try {
                    const files = await xmlFilesFromDataTransfer(e.dataTransfer);
                    if (!files.length) {
                      toast.error("No se encontraron archivos .xml en lo arrastrado");
                      return;
                    }
                    await doUploadXML(files, { allYears: folderUploadHasRelativePaths(files) });
                  } catch (err) {
                    toast.error(formatApiError(err) || "No se pudo leer la carpeta arrastrada");
                  }
                }}
              >
                <Database className="w-10 h-10 mx-auto text-slate-400 mb-2" strokeWidth={1.2} />
                <p className="text-sm text-slate-700 font-bold">Carga de CFDI al sistema</p>
                <p className="text-[11px] text-slate-500 mt-1">
                  Arrastra la carpeta o los XML aquí, o usa el botón de abajo.
                  El sistema clasifica Emitidos/Recibidos y toma el año de cada XML
                  (no depende del selector de ejercicio).
                </p>
                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xml"
                    multiple
                    onChange={(e) => doUploadXML(e.target.files || [], { allYears: false })}
                    className="hidden"
                    data-testid="carga-xml-files-input"
                  />
                  <input
                    ref={folderInputRef}
                    type="file"
                    multiple
                    onChange={(e) => doUploadXML(e.target.files || [], { allYears: true })}
                    className="hidden"
                    data-testid="carga-xml-folder-input"
                  />
                  <Button
                    onClick={() => folderRef.current?.click()}
                    disabled={dataLoadBusy || !clientId}
                    className="bg-[#0F2D5C] hover:bg-[#0b2249] text-white"
                    data-testid="carga-xml-folder"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <FolderUp className="w-4 h-4 mr-1.5" />
                    )}
                    Cargar XML
                  </Button>
                  <Button onClick={() => clearYear("cfdi")} disabled={dataLoadBusy || !clientId || totalCfdi === 0} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Limpiar año
                  </Button>
                  <Button
                    type="button"
                    onClick={runReconcileIntegridad}
                    disabled={dataLoadBusy || reconcileBusy || !clientId}
                    variant="outline"
                    data-testid="carga-reconcile-xml-integrity"
                  >
                    {reconcileBusy ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1.5" />
                    )}
                    Recalcular XML en BD
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-2 m-0">
                  ¿Solo unos XML del ejercicio {year}?{" "}
                  <button
                    type="button"
                    className="underline text-slate-700 hover:text-[#0F2D5C] disabled:opacity-50 bg-transparent border-0 p-0 cursor-pointer font-inherit text-[10px]"
                    disabled={dataLoadBusy || !clientId}
                    onClick={() => fileRef.current?.click()}
                    data-testid="carga-xml-files-link"
                  >
                    Elegir archivos sueltos
                  </button>
                </p>

                {xmlProgress?.open && xmlProgress.minimized ? (
                  <button
                    type="button"
                    className="mt-3 w-full max-w-md mx-auto flex items-center gap-2 rounded-md border border-blue-200 bg-white px-3 py-2 text-[10px] text-slate-600"
                    onClick={() => setXmlProgress((p) => (p ? { ...p, minimized: false } : p))}
                    data-testid="carga-xml-progress-minimized"
                  >
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                    <span className="font-semibold uppercase">UPLOAD</span>
                    <span className="ml-auto tabular-nums">
                      {Math.min(
                        100,
                        Math.round(((xmlProgress.batch || 0) / Math.max(1, xmlProgress.totalBatches)) * 100),
                      )}
                      %
                    </span>
                    <span className="text-slate-400">· ver avance</span>
                  </button>
                ) : null}

                {xmlProgress?.open && !xmlProgress.minimized ? (() => {
                  const pct = Math.min(
                    100,
                    Math.round(((xmlProgress.batch || 0) / Math.max(1, xmlProgress.totalBatches)) * 100),
                  );
                  const isError = xmlProgress.status === "error";
                  const isDone = xmlProgress.status === "done";
                  const isCancelled = xmlProgress.status === "cancelled";
                  const isBusy = xmlProgress.status === "running" || xmlProgress.status === "cancelling";
                  const stepLabel = isError
                    ? "ERROR"
                    : isDone
                      ? "DONE"
                      : isCancelled
                        ? "CANCELLED"
                        : xmlProgress.status === "cancelling"
                          ? "CANCELLING"
                          : "UPLOAD";
                  const message = isError
                    ? "No se pudo completar la carga. Revise el aviso o reintente."
                    : isDone
                      ? `Listo: ${(xmlProgress.inserted || 0).toLocaleString("es-MX")} nuevos, ${(xmlProgress.duplicated || 0).toLocaleString("es-MX")} actualizados.`
                      : isCancelled
                        ? `Cancelada: ${(xmlProgress.inserted || 0).toLocaleString("es-MX")} nuevos, ${(xmlProgress.duplicated || 0).toLocaleString("es-MX")} actualizados hasta el momento.`
                        : xmlProgress.status === "cancelling"
                          ? "Cancelando lotes pendientes…"
                          : `Lote ${xmlProgress.batch || 0}/${xmlProgress.totalBatches} · ${(xmlProgress.filesTotal || 0).toLocaleString("es-MX")} archivo(s) · ${(xmlProgress.inserted || 0).toLocaleString("es-MX")} nuevos · ${(xmlProgress.duplicated || 0).toLocaleString("es-MX")} actualizados${(xmlProgress.errors || 0) > 0 ? ` · ${xmlProgress.errors.toLocaleString("es-MX")} con error` : ""}`;
                  return (
                    <div
                      className={`mt-4 mx-auto max-w-md w-full text-left rounded-md border p-3 space-y-2 ${
                        isError ? "border-red-200 bg-red-50/80" : "border-blue-200 bg-white"
                      }`}
                      data-testid="carga-xml-progress-panel"
                      role="status"
                      aria-live="polite"
                    >
                      <div
                        className={`flex items-center gap-2 text-[10px] ${
                          isError ? "text-red-900" : "text-slate-600"
                        }`}
                      >
                        {isError ? (
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />
                        ) : isBusy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                        ) : isDone ? (
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                        )}
                        <span className="font-semibold uppercase">{stepLabel}</span>
                        {!isError ? (
                          <span className="ml-auto tabular-nums">{pct}%</span>
                        ) : null}
                      </div>
                      {!isError ? (
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full bg-blue-600 transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      ) : null}
                      <p className={`text-[10px] m-0 ${isError ? "text-red-950" : "text-slate-600"}`}>
                        {message}
                      </p>
                      {isBusy ? (
                        <p className="text-[10px] m-0 text-slate-500">
                          Continuar oculta este panel; la carga sigue. Cancelar detiene los lotes pendientes.
                        </p>
                      ) : null}
                      <div className="flex flex-wrap gap-2 pt-0.5">
                        {isBusy ? (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px] border-red-200 text-red-700 hover:bg-red-50"
                              onClick={cancelXmlUpload}
                              disabled={xmlProgress.status === "cancelling"}
                              data-testid="carga-xml-progress-cancel"
                            >
                              Cancelar
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px] border-blue-200"
                              onClick={continueXmlProgress}
                              data-testid="carga-xml-progress-continue"
                            >
                              Continuar
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-[10px] border-blue-200"
                            onClick={closeXmlProgress}
                            data-testid="carga-xml-progress-close"
                          >
                            Continuar
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })() : null}

                <p className="text-[10px] text-slate-500 mt-3 m-0 max-w-lg mx-auto leading-relaxed">
                  Si ya cargó XML y no aparecen en Mis CFDI, use <strong>Recalcular XML en BD</strong>: busca
                  archivos ya almacenados y corrige el estado de integridad sin volver a subirlos.
                </p>
                {lastResult?.kind === "xml" && (lastResult.skipped_rfc > 0 || lastResult.skipped_year > 0) ? (
                  <div
                    className="mt-4 text-left max-w-2xl mx-auto rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950"
                    data-testid="carga-xml-skip-summary"
                  >
                    <p className="m-0 font-semibold">
                      Omitidos en la última carga
                      {lastResult.client_rfc ? ` · RFC cliente ${lastResult.client_rfc}` : ""}
                    </p>
                    <p className="m-0 mt-1">
                      {lastResult.skipped_rfc || 0} por RFC · {lastResult.skipped_year || 0} por año
                      (ejercicio {year}).
                    </p>
                    {Array.isArray(lastResult.skipped_details) && lastResult.skipped_details.length > 0 ? (
                      <ul className="m-0 mt-1 pl-4 list-disc space-y-0.5">
                        {lastResult.skipped_details.slice(0, 5).map((row, idx) => (
                          <li key={`${row.filename || "f"}-${idx}`}>
                            {row.reason === "year_mismatch"
                              ? `${row.filename || "XML"}: año XML ${row.xml_year} ≠ ejercicio ${row.expected}`
                              : `${row.filename || "XML"}: ${row.rfc_xml || "sin RFC"} (esperado ${row.expected || lastResult.client_rfc || "—"})`}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <p className="m-0 mt-1 text-amber-900/80">
                      Si el RFC del cliente está mal en Clientes, corríjalo y vuelva a cargar. Para varios
                      ejercicios use «Cargar XML» (seleccionar carpeta).
                    </p>
                  </div>
                ) : null}
                {pendingXmlDetails.length > 0 ? (
                  <div className="mt-4 text-left max-w-2xl mx-auto">
                    <p className="text-xs font-semibold text-amber-900 m-0 mb-2">
                      Comprobantes aún sin XML ({pendingXmlDetails.length})
                    </p>
                    <XmlPendingDetailsList items={pendingXmlDetails} />
                  </div>
                ) : null}
              </div>
            </div>
            </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="excel" className="border-slate-200 px-0">
            <AccordionTrigger data-testid="tab-carga-excel">
              <span className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Carga de Reporte en Excel (Contalink / Control IVA)
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4">
            <Accordion type="single" collapsible className="mb-4 rounded-sm border border-sky-200 bg-sky-50/90">
              <AccordionItem value="proceso-contalink" className="border-0">
                <AccordionTrigger
                  className="px-4 py-3 text-sm text-slate-800 leading-relaxed hover:no-underline [&[data-state=open]]:pb-2"
                  data-testid="carga-excel-contalink-proceso-notice-trigger"
                >
                  <span className="flex items-start gap-3 text-left">
                    <Info className="w-5 h-5 shrink-0 text-sky-700 mt-0.5" strokeWidth={1.75} aria-hidden />
                    <span className="font-semibold text-slate-900">
                      Proceso recomendado: información generada con reportes Contalink / Control IVA
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent
                  className="px-4 pb-3 pt-0 text-sm text-slate-800 leading-relaxed"
                  data-testid="carga-excel-contalink-proceso-notice"
                >
                  <div className="pl-8 min-w-0" role="note">
                    <p className="text-slate-700 mb-3 m-0">
                      Si utiliza el sistema contable <strong>Contalink</strong>, los archivos de esta sección corresponden
                      al <strong>reporte de impuestos (Control IVA)</strong> exportado desde ese entorno. Para obtener
                      resultados coherentes en la <strong>confronta</strong> con los CFDI cargados en Visor Fiscal, le
                      sugerimos el siguiente orden de trabajo:
                    </p>
                    <ol className="list-decimal pl-5 space-y-2 m-0 text-slate-800">
                      <li>
                        Tenga <strong>conciliados bancos y caja</strong> y la <strong>información contabilizada</strong> en
                        Contalink antes de generar reportes fiscales.
                      </li>
                      <li>
                        En el módulo de <strong>Contabilidad</strong>, acceda a la pestaña <strong>Reporte de impuestos</strong>{" "}
                        (o la ruta equivalente donde se emitan los reportes de IVA).
                      </li>
                      <li>
                        Genere el reporte de <strong>ingresos</strong> del periodo a analizar (por ejemplo, enero a diciembre
                        de 2026), expórtelo a <strong>Excel</strong> y guárdelo en una carpeta de trabajo. Repita el mismo
                        procedimiento para <strong>egresos</strong> y para <strong>nóminas</strong>, de modo que disponga de
                        un archivo por tipo de información.
                      </li>
                      <li>
                        Los archivos <strong>Excel</strong> así obtenidos son los que debe <strong>cargar en esta sección</strong>.
                        Una vez integrados, podrá ejecutar la <strong>confronta</strong> con los CFDI del SAT ya importados
                        en el sistema.
                      </li>
                    </ol>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
                <p className="text-xs text-slate-600">
                Lo habitual es la <strong>plantilla de 3 hojas</strong> (pestaña <strong>Plantillas para cargas en Excel (Formato Estandar)</strong>) o el Excel del reporte <strong>Control de IVA</strong> / <strong>Contalink</strong> (una hoja), según lo que ya tengas.
                Usa <strong>Carga por CSV y otros sistemas</strong> si tu sistema exporta otro layout.
              </p>
              <p className="text-xs text-slate-700 rounded-sm border border-emerald-200 bg-emerald-50/80 px-3 py-2">
                <strong>Contalink / Control IVA:</strong> en <strong>Etiketa/Etiqueta</strong> use cuenta SAT (<code className="font-mono text-[10px] bg-white/80 px-1 rounded">401-…</code> ingreso;{" "}
                <code className="font-mono text-[10px] bg-white/80 px-1 rounded">601-…</code> / <code className="font-mono text-[10px] bg-white/80 px-1 rounded">701-…</code> egreso); la cuenta manda sobre archivo y hoja.
                <span className="block mt-1"><strong>Plantilla estándar 3 hojas:</strong> sin columna Etiqueta; cada hoja clasifica sola. En otros Excel puede poner <strong>Ingreso (ventas)</strong>, <strong>Egreso (gastos)</strong> o <strong>Nominas</strong> en Etiqueta; use la <strong>fecha de cobro/pago</strong> del periodo para complementos PPD.</span>
                <span className="block mt-1"><strong>{CONCILIACION_MANUAL_CFDI_TITLE}</strong>: según CFDI emitido/recibido y pagos parciales/completos; confronta cruza con complementos de pago del SAT en el mismo periodo.</span>
              </p>
              <ExcelUploadIntentPicker
                value={excelUploadIntent}
                onChange={(v) => {
                  setExcelUploadIntent(v);
                  try { localStorage.setItem("vf:excel_upload_intent", v); } catch { /* ignore */ }
                }}
                disabled={dataLoadBusy}
                lastImport={excelStats?.last_import}
                year={year}
              />
              <ExcelCargaResumenPanel uploadIntent={excelUploadIntent} lastResult={lastResult} />
              <div
                className="bg-slate-50 border border-slate-200 border-dashed rounded-md p-8 text-center"
                data-testid="carga-excel-contalink-dropzone"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (dataLoadBusy || !clientId) {
                    if (!clientId) toast.error("Seleccione una empresa arriba para cargar Excel.");
                    return;
                  }
                  const resolved = resolveExcelFiles(e.dataTransfer?.files);
                  if (!resolved.ok) {
                    const msg = resolved.reason === "empty"
                      ? "Suelte uno o más archivos Excel (.xls / .xlsx)."
                      : EXCEL_INVALID_FILE_MSG;
                    setExcelStatusMsg(msg);
                    toast.error(msg, { duration: 10000 });
                    return;
                  }
                  const startMsg = `Arrastre: ${resolved.files.length} Excel detectado(s). Iniciando carga…`;
                  setExcelStatusMsg(startMsg);
                  toast.info(startMsg, { id: "excel-upload-progress", duration: 8000 });
                  await uploadExcelFiles(resolved.files);
                }}
              >
                <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" strokeWidth={1.2} />
                <p className="text-sm text-slate-700">Excel .xls / .xlsx (3 hojas o Control IVA)</p>
                <p className="text-[10px] text-slate-500 mt-1 m-0 max-w-xl mx-auto">
                  Puede cargar un <strong>archivo</strong> o una <strong>carpeta</strong> con varios reportes
                  Contalink (ingresos, egresos, nóminas). Solo se importan los .xls/.xlsx de esa carpeta.
                  También puede <strong>arrastrar y soltar</strong> archivos Excel aquí.
                </p>
                {busy && excelProgress ? (
                  <div
                    className="mt-3 mx-auto max-w-lg rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-950 flex items-center justify-center gap-2"
                    data-testid="carga-excel-upload-progress"
                    role="status"
                  >
                    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                    Cargando Excel {excelProgress.current}/{excelProgress.total}: {excelProgress.name}
                  </div>
                ) : busy ? (
                  <div
                    className="mt-3 mx-auto max-w-lg rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-950 flex items-center justify-center gap-2"
                    data-testid="carga-excel-upload-progress"
                    role="status"
                  >
                    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                    Cargando Excel… espere el resumen al terminar.
                  </div>
                ) : null}
                {excelStatusMsg ? (
                  <p
                    className={`mt-2 mx-auto max-w-lg text-[11px] rounded-sm px-3 py-2 m-0 border ${
                      excelStatusMsg.startsWith("Carga exitosa") || excelStatusMsg.startsWith("Carga de carpeta exitosa")
                        ? "text-emerald-900 bg-emerald-50 border-emerald-200 font-medium"
                        : excelStatusMsg.startsWith("Error")
                          ? "text-red-800 bg-red-50 border-red-200"
                          : "text-slate-700 bg-white border-slate-200"
                    }`}
                    data-testid="carga-excel-status-msg"
                    role="status"
                  >
                    {excelStatusMsg}
                  </p>
                ) : null}
                <div className="flex gap-2 justify-center mt-4 flex-wrap">
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept={EXCEL_FILE_ACCEPT}
                    onChange={onExcelUpload}
                    className="hidden"
                    data-testid="carga-excel-contalink-input"
                  />
                  <input
                    ref={excelFolderInputRef}
                    type="file"
                    multiple
                    // Igual que carga XML: atributos en JSX (no solo setAttribute).
                    webkitdirectory=""
                    directory=""
                    onChange={onExcelFolderUpload}
                    className="hidden"
                    data-testid="carga-excel-folder-input"
                  />
                  <Button
                    type="button"
                    onClick={() => openExcelFilePicker("contalink")}
                    disabled={dataLoadBusy || !clientId}
                    className="bg-[#0F2D5C] hover:bg-[#0b2249] text-white"
                    data-testid="carga-excel-contalink-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                    {busy ? "Cargando…" : "Cargar archivo Excel"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openExcelFolderPicker}
                    disabled={dataLoadBusy || !clientId}
                    className="border-[#0F2D5C]/40 text-[#0F2D5C]"
                    data-testid="carga-excel-folder-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FolderUp className="w-4 h-4 mr-1.5" />}
                    Cargar carpeta Excel
                  </Button>
                  <Button onClick={() => clearYear("excel")} disabled={dataLoadBusy || !clientId || !excelStats?.count} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Limpiar año
                  </Button>
                </div>
              </div>
            </div>
            </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="plantilla_excel" className="border-slate-200 px-0">
            <AccordionTrigger data-testid="tab-carga-plantilla-excel">
              <span className="flex items-center gap-2">
                <FileDown className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Plantillas para cargas en Excel (Formato Estandar)
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4 space-y-4">
            <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-sm font-bold text-[#0F2D5C] flex items-center gap-2">
                  <Table2 className="w-4 h-4" /> 1. Descargar plantilla simplificada (3 hojas)
                </h3>
                <p className="text-[11px] text-slate-600 mt-2">
                  Un solo archivo <code className="font-mono text-[10px] bg-slate-100 px-1 rounded">.xlsx</code> con tres hojas obligatorias:
                  <strong> Ingresos</strong>, <strong> Egresos</strong> y <strong> Nominas</strong>, con el mínimo de columnas para que el contador cargue o copie solo lo esencial.
                </p>
                <p className="text-[11px] text-amber-800 font-medium mt-2">
                  Mínimo para importar: <strong>Tipo</strong> y <strong>Folio Fiscal (UUID)</strong> en cada fila de datos.
                  No hace falta columna Etiqueta ni cuenta contable: cada hoja clasifica la fila (Ventas / Gasto / Nóminas). Opcionalmente puede poner en Etiqueta el texto <strong>Ingreso</strong>, <strong>Egreso</strong> o <strong>Nominas</strong>.
                </p>
                <div className="grid gap-3 sm:grid-cols-3 text-[10px] text-slate-700 mt-3">
                  {(["Ingresos", "Egresos", "Nominas"]).map((nombre) => (
                    <div key={nombre} className="rounded border border-slate-200 bg-slate-50 p-2 max-h-40 overflow-y-auto">
                      <p className="font-semibold text-[#0F2D5C] mb-1">{nombre}</p>
                      <ul className="list-disc list-inside space-y-0.5 font-mono leading-snug">
                        {(PLANTILLA_3HOJAS_HEADERS[nombre] || []).map((h) => (
                          <li key={h}>{h}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button type="button" onClick={() => downloadReportBlob("/reports/plantilla-excel-3-hojas", undefined, "Plantilla_Control_IVA_3_hojas.xlsx")} className="bg-[#0F2D5C] hover:bg-[#0b2249] text-white h-9 text-xs">
                    <FileDown className="w-4 h-4 mr-1.5" /> Descarga plantilla en blanco
                  </Button>
                  <Button type="button" variant="outline" onClick={() => downloadReportBlob("/reports/plantilla-excel-3-hojas", { with_example: true }, "Plantilla_Control_IVA_3_hojas_con_ejemplos.xlsx")} className="h-9 text-xs border-blue-300 text-blue-900">
                    <FileDown className="w-4 h-4 mr-1.5" /> Descargar con filas de ejemplo
                  </Button>
                </div>
              </div>

              <div className="pt-2">
                <h3 className="text-sm font-bold text-[#0F2D5C] flex items-center gap-2">
                  <Upload className="w-4 h-4" /> 2. Cargar Excel modificado (listo para confronta)
                </h3>
                <p className="text-[11px] text-slate-600 mt-2">
                  Sube el <code className="font-mono text-[10px] bg-slate-100 px-1 rounded">.xlsx</code> o <code className="font-mono text-[10px] bg-slate-100 px-1 rounded">.xls</code> después de capturar tus datos.
                  Lo recomendado es esta <strong>plantilla de 3 hojas</strong>. El sistema también acepta el formato antiguo de <strong>una sola hoja</strong> tipo Contalink (<code className="font-mono text-[10px] bg-slate-100 px-1 rounded">control_iva</code>) si ya lo tienes armado.
                </p>
                <ExcelUploadIntentPicker
                  value={excelUploadIntent}
                  onChange={(v) => {
                    setExcelUploadIntent(v);
                    try { localStorage.setItem("vf:excel_upload_intent", v); } catch { /* ignore */ }
                  }}
                  disabled={dataLoadBusy}
                  lastImport={excelStats?.last_import}
                  year={year}
                />
                <ExcelCargaResumenPanel uploadIntent={excelUploadIntent} lastResult={lastResult} />
                <div className="bg-slate-50 border border-slate-200 border-dashed rounded-md p-6 text-center mt-3">
                  <input
                    ref={excelPlantillaInputRef}
                    type="file"
                    accept={EXCEL_FILE_ACCEPT}
                    onChange={onExcelUpload}
                    className="hidden"
                    data-testid="carga-excel-plantilla-input"
                  />
                  <Button
                    type="button"
                    onClick={() => openExcelFilePicker("plantilla")}
                    disabled={dataLoadBusy || !clientId}
                    className="bg-[#0F2D5C] hover:bg-[#0b2249] text-white"
                    data-testid="carga-excel-plantilla-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                    Seleccionar archivo Excel modificado
                  </Button>
                  <Button type="button" onClick={() => clearYear("excel")} disabled={dataLoadBusy || !clientId || !excelStats?.count} variant="outline" className="ml-2 border-red-200 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Limpiar filas Excel ({year})
                  </Button>
                </div>
              </div>
            </div>
            </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="csv" className="border-slate-200 px-0">
            <AccordionTrigger data-testid="tab-carga-csv">
              <span className="flex items-center gap-2">
                <Table2 className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Carga por CSV y otros sistemas
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4 space-y-4">
            <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
              <p className="text-xs text-slate-600">
                Para sistemas que exportan <strong>CSV</strong> (conciliación, auxiliares con UUID, etc.).
                El archivo debe incluir como mínimo las columnas equivalentes a <strong>Tipo</strong> y <strong>Folio fiscal (UUID)</strong>.
                La plantilla Excel de 3 hojas está en <strong>Plantillas para cargas en Excel (Formato Estandar)</strong>.
                Si vienes de otro ERP, descarga la plantilla CSV mínima debajo, completa datos y súbela con el paso 1.
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-6 space-y-3">
                <p className="text-sm text-slate-700 font-semibold text-center">Plantilla mínima CSV (otros sistemas)</p>
                <p className="text-[11px] text-slate-600 text-center max-w-lg mx-auto">
                  Solo encabezados esenciales. Copia datos desde tu ERP y luego sube el archivo con <strong>1. Subir CSV</strong> más abajo;
                  {" "}el mapeo de columnas en el paso 2 puede reconocer estos nombres automáticamente.
                </p>
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button type="button" variant="outline" onClick={() => downloadReportBlob("/excel/plantilla-minima", { direccion: "ingreso" }, "plantilla_minima_ingresos.csv")}>
                    <FileDown className="w-4 h-4 mr-1.5" /> CSV ingresos
                  </Button>
                  <Button type="button" variant="outline" onClick={() => downloadReportBlob("/excel/plantilla-minima", { direccion: "egreso" }, "plantilla_minima_egresos.csv")}>
                    <FileDown className="w-4 h-4 mr-1.5" /> CSV egresos
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Flujo contable del archivo</Label>
                  <Select value={csvDireccion} onValueChange={setCsvDireccion}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ingreso">Ingresos (ventas / cobros)</SelectItem>
                      <SelectItem value="egreso">Egresos (compras / pagos)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col justify-end">
                  <input ref={csvRef} type="file" accept=".csv,.txt" onChange={onCsvFileSelected} className="hidden" />
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" onClick={() => csvRef.current?.click()} disabled={dataLoadBusy || !clientId} variant="outline" className="h-9 text-xs">
                      {csvBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      1. Subir CSV (vista previa)
                    </Button>
                    {csvPreview && (
                      <span className="text-[11px] text-slate-500 self-center">
                        ~{csvPreview.data_row_count} filas · delimitador &quot;{csvPreview.delimiter === "\t" ? "TAB" : csvPreview.delimiter}&quot;
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {csvPreview && (
                <div className="space-y-3 border border-slate-200 rounded-md p-4 bg-slate-50/80">
                  <p className="text-xs font-semibold text-[#0F2D5C]">2. Mapeo de columnas</p>
                  <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-sm bg-white">
                    <table className="w-full text-[10px]">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          {csvPreview.headers.map((h, i) => (
                            <th key={i} className="text-left px-2 py-1 border-b font-mono whitespace-nowrap max-w-[140px] truncate" title={h}>
                              {i}: {h || "—"}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.sample_rows.map((row, ri) => (
                          <tr key={ri} className="border-t border-slate-100">
                            {row.map((c, ci) => (
                              <td key={ci} className="px-2 py-0.5 font-mono text-slate-600 max-w-[140px] truncate" title={c}>{c}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 max-h-64 overflow-y-auto pr-1">
                    {fieldCatalog.map((f) => (
                      <div key={f.key} className="flex items-center gap-2">
                        <Label className={`text-[10px] w-[44%] shrink-0 leading-tight ${f.required ? "text-amber-800 font-semibold" : "text-slate-600"}`}>
                          {f.label}{f.required ? " *" : ""}
                        </Label>
                        <Select
                          value={csvMapping[f.key] != null && csvMapping[f.key] !== "" ? String(csvMapping[f.key]) : "__none__"}
                          onValueChange={(v) => setCsvMapping((prev) => {
                            const next = { ...prev };
                            if (v === "__none__") delete next[f.key];
                            else next[f.key] = v;
                            return next;
                          })}
                        >
                          <SelectTrigger className="h-8 text-[10px] flex-1"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">(ninguna)</SelectItem>
                            {csvPreview.headers.map((h, i) => (
                              <SelectItem key={i} value={String(i)}>{i}: {(h || "").slice(0, 48) || "—"}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <Button type="button" onClick={submitCsvUpload} disabled={dataLoadBusy || !clientId || !csvFileHold} className="h-9 bg-[#0F2D5C] hover:bg-[#0b2249] text-white text-xs">
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    3. Importar CSV al sistema
                  </Button>
                </div>
              )}
            </div>
            </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="conciliacion-manual" className="border-slate-200 px-0 border-b-0">
            <AccordionTrigger data-testid="tab-carga-conciliacion-manual">
              <span className="flex items-center gap-2">
                <ListChecks className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Conciliación manual de CFDI (Sistema / Excel) para Confronta
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4">
                <p className="text-[12px] text-slate-600 mb-4 leading-relaxed">
                  Concilie UUID entre Excel, CSV y complementos de pago cuando hay doble fuente. Usa el cliente y ejercicio seleccionados arriba.
                </p>
                <ConciliacionManualCFDI
                  embedMode="carga"
                  clientId={clientId}
                  setClientId={setClientId}
                  year={year}
                  setYear={setYear}
                  clients={clients}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

        </Accordion>

        {!cli && (
          <div className="text-center text-sm text-slate-500 py-4">
            Selecciona un cliente para comenzar.
          </div>
        )}
      </div>

      <AlertDialog open={tooLargeDialog.open} onOpenChange={(open) => setTooLargeDialog((s) => ({ ...s, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              Archivos omitidos por tamaño
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-700">
                <p>Los siguientes archivos superan el límite permitido y no fueron procesados:</p>
                <ul className="list-disc pl-5 space-y-1 max-h-48 overflow-y-auto">
                  {(tooLargeDialog.items || []).map((item) => (
                    <li key={item.filename + item.message}>{item.message}</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Entendido</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Pillar({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-start">
      <Icon className="w-8 h-8 text-[#0F2D5C] mb-1" strokeWidth={1.5}/>
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#0F2D5C]" style={{fontFamily:"'Chivo'"}}>{title}</div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#3DA873]" style={{fontFamily:"'Chivo'"}}>{subtitle}</div>
    </div>
  );
}
